import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';

import { showChromeToolbarPlaybackControls } from '../classes/ChromeToolbarPlaybackControlsSingleton';
import { logger } from '../datadog/logger';
import { useVideoPlatformCallback } from '../hooks/useVideoPlatform';
import type { RoomDelegator } from '../video-sdk';
import { VideoPlatform, roomTokenAtom, useJoinRoom } from '../video-sdk';
import { JoinRoomOptions } from '../video-sdk/core/adapter';
import { zoomTokenAtom } from '../video-sdk/zoom';

export type ConnectRoomCallback = (
  displayName: string,
  options?: {
    tokenOverride?: string;
    onError?: (error: unknown) => void;
  } & JoinRoomOptions,
) => Promise<RoomDelegator | undefined>;

/**
 * Returns a callback function that connects to a new Twilio room. This callback
 * will not set/handle any room listeners.
 */
export function useConnectRoomCallback(): [ConnectRoomCallback, { loading?: boolean }] {
  const [loading, setLoading] = useState(false);
  const joinRoom = useJoinRoom();
  const getVideoPlatform = useVideoPlatformCallback();
  const getRoomToken = useAtomCallback(
    useCallback(
      (get) => {
        return getVideoPlatform() === VideoPlatform.Zoom ? get(zoomTokenAtom) : get(roomTokenAtom);
      },
      [getVideoPlatform],
    ),
  );

  const callback = useCallback<ConnectRoomCallback>(
    async function connectRoom(displayName: string, { tokenOverride, onError, roomValidators = [] } = {}) {
      const token = tokenOverride || getRoomToken();
      if (!token) return;
      setLoading(true);
      try {
        const room = await joinRoom(token, displayName, { roomValidators });
        showChromeToolbarPlaybackControls();
        return room;
      } catch (error) {
        if (onError) {
          onError(error);
          return;
        }
        logger.error('Failed to connect to room', {}, error as Error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [callback, { loading }];
}
